/**
 * Application Routes Configuration
 * With JWT Authentication
 */

import { useEffect, useState } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { MainLayout } from '../components/Layout/MainLayout';
import { Dashboard } from '../pages/Dashboard';
import { ProjectsList } from '../pages/ProjectsList';
import { ProjectWorkspace } from '../pages/ProjectWorkspace';
import { SettingsPage } from '../pages/SettingsPage';
import { LoginPage } from '../pages/LoginPage';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../lib/api';

/**
 * Hook to get auth state - compatible with existing code
 */
const useAuth = () => {
  const { isAuthenticated, user } = useAuthStore.getState();
  return { 
    isAuthenticated, 
    user: user || { name: 'Guest', email: '' } 
  };
};

/**
 * Protected Route wrapper - checks authentication
 * Also handles token refresh on page reload
 */
function ProtectedRoute() {
  const { isAuthenticated, isLoading, accessToken, updateTokens, logout, setLoading } = useAuthStore();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasTriedRefresh, setHasTriedRefresh] = useState(false);
  
  // Try to refresh token on mount if authenticated but no accessToken
  useEffect(() => {
    const tryRefreshToken = async () => {
      // Only try once
      if (hasTriedRefresh) return;
      
      // If we have isAuthenticated but no accessToken, try to refresh
      if (isAuthenticated && !accessToken && !isRefreshing) {
        setIsRefreshing(true);
        setLoading(true);
        
        try {
          const response = await authApi.refresh();
          const newAccessToken = response.data?.data?.accessToken;
          
          if (newAccessToken) {
            updateTokens(newAccessToken);
          } else {
            // No token received, logout
            logout();
          }
        } catch (error) {
          console.error('Token refresh failed:', error);
          // Refresh failed, logout user
          logout();
        } finally {
          setIsRefreshing(false);
          setLoading(false);
          setHasTriedRefresh(true);
        }
      } else {
        setHasTriedRefresh(true);
      }
    };
    
    tryRefreshToken();
  }, [isAuthenticated, accessToken, hasTriedRefresh, isRefreshing, updateTokens, logout, setLoading]);
  
  // Show loading spinner while checking auth or refreshing token
  if (isLoading || isRefreshing || (isAuthenticated && !accessToken && !hasTriedRefresh)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <Outlet />;
}

/**
 * Public Route wrapper - redirect if already logged in
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

/**
 * Application Router
 */
export const router = createBrowserRouter([
  // Public routes
  {
    path: '/login',
    element: (
      <PublicRoute>
        <LoginPage />
      </PublicRoute>
    ),
  },
  
  // Protected routes with MainLayout
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <MainLayout />,
        children: [
          {
            path: '/',
            element: <Dashboard />,
          },
          {
            path: '/projects',
            element: <ProjectsList />,
          },
          {
            path: '/projects/:projectId/*',
            element: <ProjectWorkspace />,
          },
          {
            path: '/settings',
            element: <SettingsPage />,
          },
        ],
      },
    ],
  },
  
  // Catch-all redirect to dashboard
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

export { useAuth };
